<template>
  <v-row :style="{ height: `${height}px` }">
    <v-col cols="7">
      <v-row class="h-60">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="d-flex pa-1 bg">
              <div class="mr-auto d-flex">
                <span class="px-1">{{ `${$t('sod.ttl.matrix')} ${$t('common.list')}` }}</span>
              </div>
              <div class="mr-auto d-flex">
                <!-- eslint-disable-next-line prettier/prettier -->
                <v-text-field
                  v-model="matrix.srchNm" :placeholder="this.$t('sod.filter.matrix.nm')"
                  class="text-full-width" outlined dense hide-details clearable
                />
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="ml-2" x-small color="primary" @click="fetchMatrixList()">
                      <v-icon small>mdi-magnify</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('common.retrieve') }}</span>
                </v-tooltip>
              </div>
              <div class="top-button-group">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="mr-2" x-small right color="primary" @click="addMatrix">
                      <v-icon small>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('common.add') }}</span>
                </v-tooltip>
              </div>
            </v-card-title>
            <v-card-text class="pa-0">
              <!-- eslint-disable-next-line prettier/prettier -->
              <v-data-table
                v-model="matrix.selected" :headers="matrixHeaders" :items="matrix.items" item-key="matrixId"
                :options.sync="matrix.options" :items-per-page="matrix.paging.rowsPerPage" :loading="matrix.loading"
                class="elevation-1 content" must-sort fixed-header dense hide-default-footer single-select
                :height="dataListHeight" @click:row="matrixHighlight"
              >
                <template #item.riskLevel="{ item }">
                  <div class="color-cell" :style="riskLevelColorStyle(item.riskLevel)">
                    {{ $t(`sod.risk.level.${item.riskLevel}`) }}
                  </div>
                </template>
                <template #item.isActive="{ item }">
                  <v-simple-checkbox :value="item.isActive ? true : false" disabled></v-simple-checkbox>
                </template>
                <template #item.actions="{ item }">
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <!-- eslint-disable-next-line prettier/prettier -->
                      <v-icon
                        v-on="on" small class="mr-2" color="primary"
                        @click="editMatrix(item)"
                      >
                        mdi-pencil-box-multiple-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('common.edit') }}</span>
                  </v-tooltip>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <!-- eslint-disable-next-line prettier/prettier -->
                      <v-icon
                        v-on="on" small color="primary"
                        :disabled="item.isSstm == 1"
                        @click="deleteMatrix(item)"
                      >
                        mdi-trash-can-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('common.delete') }}</span>
                  </v-tooltip>
                </template>
                <template #footer>
                  <pagination :paging.sync="matrix.paging" :length="matrix.length" :disabled="matrix.loading" />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="h-40">
        <v-col cols="12">
          <v-row v-show="matrix.showForm">
            <v-col cols="12"><matrix-form :matrix-id="matrix.selectedId" @close="closeMatrixForm"/></v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <!-- eslint-disable-next-line prettier/prettier -->
              <duty-row-auth-list
                :title="matrix.rowAuthTitle" :mode="'LIST'"
                :duty-id="matrix.selectedRowDutyId" :height="lowerLeftHeight"
              />
            </v-col>
            <v-col cols="6">
              <!-- eslint-disable-next-line prettier/prettier -->
              <duty-col-auth-list
                :title="matrix.colAuthTitle" :mode="'LIST'"
                :duty-id="matrix.selectedColDutyId" :height="lowerLeftHeight"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="5">
      <v-row class="h-60">
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="d-flex pa-1 bg" outlined>
              <div class="mr-auto d-flex">
                <span class="px-1">{{ $t('sod.ttl.duty') }}</span>
              </div>
              <div class="mr-auto d-flex">
                <!-- eslint-disable-next-line prettier/prettier -->
                <v-text-field
                  v-model="duty.srchNm" :placeholder="this.$t('sod.filter.duty.nm')"
                  class="text-full-width" outlined dense hide-details clearable
                />
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="ml-2" x-small color="primary" @click="fetchDutyList()">
                      <v-icon small>mdi-magnify</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('common.retrieve') }}</span>
                </v-tooltip>
              </div>
              <div class="top-button-group">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="mr-2" x-small right color="primary" @click="addDuty">
                      <v-icon small>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('common.add') }}</span>
                </v-tooltip>
              </div>
            </v-card-title>
            <v-card-text class="pa-0">
              <!-- eslint-disable-next-line prettier/prettier -->
              <v-data-table
                v-model="duty.selected" :headers="dutyHeaders" :items="duty.items" item-key="dutyId"
                :options.sync="duty.options" :items-per-page="duty.paging.rowsPerPage" :loading="duty.loading"
                class="elevation-1 content" fixed-header dense hide-default-footer single-select :height="dataListHeight"
                @click:row="dutyHighlight"
              >
                <template #item.actions="{ item }">
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <!-- eslint-disable-next-line prettier/prettier -->
                      <v-icon
                        v-on="on" small class="mr-2" color="primary"
                        :disabled="item.isSstm == 1"
                        @click="editDuty(item)"
                      >
                        mdi-pencil-box-multiple-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('common.edit') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <!-- eslint-disable-next-line prettier/prettier -->
                      <v-icon
                        v-on="on" small color="primary"
                        :disabled="item.isSstm == 1"
                        @click="deleteDuty(item)"
                      >
                        mdi-trash-can-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('common.delete') }}</span>
                  </v-tooltip>
                </template>
                <template #footer>
                  <pagination :paging.sync="duty.paging" :length="duty.length" :disabled="duty.loading" />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="h-40">
        <v-col cols="12">
          <v-row v-show="duty.showForm">
            <v-col cols="12">
              <duty-form :duty-id="duty.selectedId" @close="closeDutyForm" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <!-- eslint-disable-next-line prettier/prettier -->
              <duty-auth-list
                :title="duty.authTitle" :mode="'EDIT'"
                :duty-id="duty.selectedId" :height="lowerRightHeight"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { api } from '@/api/common/instances';

import MatrixForm from './MatrixForm.vue';
import DutyRowAuthList from './DutyAuthList.vue';
import DutyColAuthList from './DutyAuthList.vue';
import DutyForm from './DutyForm.vue';
import DutyAuthList from './DutyAuthList.vue';

export default {
  components: {
    MatrixForm,
    DutyRowAuthList,
    DutyColAuthList,
    DutyForm,
    DutyAuthList,
  },
  props: {
    height: {
      type: Number,
    },
  },
  data() {
    return {
      matrix: {
        loading: false,
        items: [],
        rowAuthTitle: 'sod.ttl.row.duty.auth',
        colAuthTitle: 'sod.ttl.col.duty.auth',
        showForm: false,
        showFormMode: '',
        selectedId: 0,
        selectedRowDutyId: 0,
        selectedColDutyId: 0,
        selected: [],
        srchNm: '',
        length: 0,
        paging: { page: 1, rowsPerPage: 50 },
        options: { sortBy: ['matrixNm'], sortDesc: [false] },
      },
      duty: {
        loading: false,
        items: [],
        authTitle: 'sod.ttl.auth',
        showFormMode: '',
        showForm: false,
        selectedId: 0,
        selected: [],
        srchNm: '',
        length: 0,
        paging: { page: 1, rowsPerPage: 50 },
        options: { sortBy: ['dutyNm'], sortDesc: [false] },
      },
    };
  },
  computed: {
    dataListHeight() {
      return this.height * 0.6 - 80;
    },
    lowerLeftHeight() {
      // 180은 MatrixForm의 높이
      if (this.matrix.showForm) return this.height * 0.4 - 180;
      else return this.height * 0.4;
    },
    lowerRightHeight() {
      // 150은 DutyForm의 높이
      if (this.duty.showForm) return this.height * 0.4 - 150;
      else return this.height * 0.4;
    },
    matrixHeaders() {
      return [
        { text: this.$t('sod.ttl.id'), value: 'matrixId', width: '70px', sortable: true, align: 'right' },
        { text: this.$t('sod.ttl.matrix') + ' ' + this.$t('sod.ttl.nm'), value: 'matrixNm', sortable: true },
        { text: this.$t('sod.ttl.risk.level'), value: 'riskLevel', width: '100px', sortable: true, align: 'center' },
        { text: this.$t('sod.ttl.row.duty') + ' ' + this.$t('sod.ttl.nm'), value: 'rowDutyNm', sortable: false },
        { text: this.$t('sod.ttl.col.duty') + ' ' + this.$t('sod.ttl.nm'), value: 'colDutyNm', sortable: false },
        { text: this.$t('sod.ttl.is.active'), value: 'isActive', width: '80px', sortable: false, align: 'center' },
        { text: '', value: 'actions', width: '74px', sortable: false, align: 'center' },
      ];
    },
    dutyHeaders() {
      return [
        { text: this.$t('sod.ttl.id'), value: 'dutyId', width: '70px', sortable: true, align: 'right' },
        { text: this.$t('sod.ttl.duty') + ' ' + this.$t('sod.ttl.nm'), value: 'dutyNm', sortable: true },
        { text: this.$t('sod.ttl.desc'), value: 'dutyDesc', sortable: false },
        { text: '', value: 'actions', width: '74px', sortable: false, align: 'center' },
      ];
    },
  },
  methods: {
    riskLevelColorStyle(riskLevel) {
      if (this.$vuetify.theme.isDark) {
        if (riskLevel == 'H') return 'color: #FF9800';
        else if (riskLevel == 'M') return 'color: #FF6F00';
        else if (riskLevel == 'L') return 'color: #E65100';
        else return 'color: #FFEB3B';
      } else {
        if (riskLevel == 'H') return 'color: #FF6F00';
        else if (riskLevel == 'M') return 'color: #FF9800';
        else if (riskLevel == 'L') return 'color: #FFEB3B';
        else return 'color: #E65100';
      }
    },
    matrixHighlight(item, { isSelected, select }) {
      select(!isSelected);
      // 선택이 됐을 때.
      if (!isSelected) {
        this.matrix.selectedId = item.matrixId;
        this.matrix.selectedRowDutyId = item.rowDutyId;
        this.matrix.selectedColDutyId = item.colDutyId;
        /*
         * "edit" 버튼이 클릭됐을 때에도, data-table의 row가 선택된다.
         * 물론 "edit" 버튼의 @click 이벤트를 @click.stop 처리해주면 data-table의 row가 선택되는 행위는 발생하지 않는다.
         * 하지만, "edit" 버튼이 클릭됐을 때 해당 row가 선택되어야 한다.
         * 그러면서도, MatrixForm이 보여진 상태에서 다른 row가 선택되면 MatrixForm이 닫히게 하고 싶다.
         * 아래 두 줄은 그러한 로직을 구현한 것이다.
         * 참고로, this.matrix.showFormMode에 'EDIT'를 할당하는 행위는 "edit" 버튼이 클릭됐을 때 이루어진다.
         */
        if (this.matrix.showFormMode != 'EDIT') this.matrix.showForm = false;
        this.matrix.showFormMode = '';
      }
    },
    addMatrix() {
      this.matrix.selectedId = 0;
      this.matrix.selected = [];
      this.matrix.showForm = true;
    },
    /**
     * selectedId가 MatrixForm에 props 'matrixId'로 전달되고,
     * MatrixForm은 props 'matrixId'가 변했을 때 반응하게 작성되었으므로,
     * selectedId가 변경되도록 0을 할당했다가 다시 선택된 matrixId를 할당해준다.
     */
    editMatrix(item) {
      this.matrix.selectedId = 0;
      // this.matrix.selectedId를 바로 바꾸어 주면
      // MatrixForm 쪽에서 watch 'matrixId'의 reactivity가 동작하지 않으므로
      // 약간의 delay를 준다.
      this.$nextTick(() => {
        this.matrix.selectedId = item.matrixId;
      });
      this.matrix.showFormMode = 'EDIT';
      this.matrix.showForm = true;
    },
    async deleteMatrix(item) {
      if (await this.$dialog.confirm(this.$t('common.delete.q'))) {
        const { status } = await api.delete('/sod/matrix', { data: { matrixId: item.matrixId } });
        if (status === 200) {
          this.$dialog.info(this.$t('common.successful'));
          this.fetchMatrixList();
          this.matrix.selectedId = 0;
          this.matrix.selectedRowDutyId = 0;
          this.matrix.selectedColDutyId = 0;
        }
      }
    },
    closeMatrixForm(optn) {
      this.matrix.showForm = false;
      if (optn == 'save') this.fetchMatrixList();
    },
    async fetchMatrixList() {
      this.matrix.loading = true;
      const params = { matrixNm: this.matrix.srchNm };
      params.page = this.matrix.paging.page;
      params.limit = this.matrix.paging.rowsPerPage;
      const {
        data: { items, pagination },
      } = await api.get('/sod/matrix-list', { params });
      this.matrix.items = items;
      this.matrix.length = pagination.totalCount;
      this.matrix.loading = false;
    },

    dutyHighlight(item, { isSelected, select }) {
      select(!isSelected);
      // 선택이 됐을 때.
      if (!isSelected) {
        this.duty.selectedId = item.dutyId;
        if (this.duty.showFormMode != 'EDIT') this.duty.showForm = false;
        this.duty.showFormMode = '';
      }
    },
    addDuty() {
      this.duty.selectedId = 0;
      this.duty.selected = [];
      this.duty.showForm = true;
    },
    /**
     * selectedId가 하위 컴포넌트에 props 'dutyId'로 전달되고,
     * 하위 컴포넌트들은 props 'dutyId'가 변했을 때 반응하게 작성되었으므로,
     * selectedId가 변경되도록 0을 할당했다가 다시 선택된 dutyId를 할당해준다.
     */
    editDuty(item) {
      this.duty.selectedId = 0;
      // this.duty.selectedId를 바로 바꾸어 주면
      // DutyForm 쪽에서 watch 'dutyId'의 reactivity가 동작하지 않으므로
      // 약간의 delay를 준다.
      this.$nextTick(() => {
        this.duty.selectedId = item.dutyId;
      });
      this.duty.showFormMode = 'EDIT';
      this.duty.showForm = true;
    },
    async deleteDuty(item) {
      if (await this.$dialog.confirm(this.$t('common.delete.q'))) {
        const { status } = await api.delete('/sod/duty', { data: { dutyId: item.dutyId } });
        if (status === 200) {
          this.$dialog.info(this.$t('common.successful'));
          this.fetchDutyList();
          this.duty.selectedId = 0;
        }
      }
    },
    closeDutyForm(optn) {
      this.duty.showForm = false;
      if (optn == 'save') this.fetchDutyList();
    },
    async fetchDutyList() {
      this.duty.loading = true;
      const params = { dutyNm: this.duty.srchNm };
      params.page = this.duty.paging.page;
      params.limit = this.duty.paging.rowsPerPage;
      const {
        data: { items, pagination },
      } = await api.get('/sod/duty-list', { params });
      this.duty.items = items;
      this.duty.length = pagination.totalCount;
      this.duty.loading = false;
    },
  },
  created() {
    this.fetchMatrixList();
    this.fetchDutyList();
  },
};
</script>

<style lang="scss" scoped>
.text-full-width {
  width: 360px;
  margin-bottom: 3px;
}
.color-cell {
  width: 100%;
  height: 15px;
}
</style>
